// =============================================================================
// SIREPPAR - Página de Perfil de Usuario
// =============================================================================

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { api, authService } from '../../services/api';

// =============================================================================
// Constantes
// =============================================================================

const rolLabels: Record<string, string> = {
  ADMINISTRADOR: 'Administrador',
  SUPERVISOR: 'Supervisor',
  ANALISTA: 'Analista',
  MESA_ENTRADAS: 'Mesa de Entradas',
  AUTORIDAD_FIRMANTE: 'Autoridad Firmante',
  AUDITOR: 'Auditor',
  APODERADO_FIRMANTE: 'Apoderado Firmante',
  APODERADO_GESTOR: 'Apoderado Gestor',
  CONSULTA_PUBLICA: 'Consulta Pública',
};

// =============================================================================
// Componente
// =============================================================================

export default function PerfilPage() {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'datos' | 'seguridad'>('datos');
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaQR, setMfaQR] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');

  // Form cambio contraseña
  const { register: registerPwd, handleSubmit: handleSubmitPwd, reset: resetPwd, watch, formState: { errors: errorsPwd } } = useForm({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');

  // Mutation cambiar contraseña
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) => 
      api.post('/usuarios/cambiar-password', data),
    onSuccess: () => {
      toast.success('Contraseña actualizada');
      resetPwd();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al cambiar contraseña');
    },
  });

  // Mutation setup MFA
  const setupMFAMutation = useMutation({
    mutationFn: () => authService.setupMFA(),
    onSuccess: (response) => {
      setMfaQR(response.data.data.qrCode);
      setMfaSecret(response.data.data.secret);
      setShowMFASetup(true);
    },
    onError: () => toast.error('Error al configurar MFA'),
  });

  // Mutation verify MFA
  const verifyMFAMutation = useMutation({
    mutationFn: (code: string) => authService.verifyMFA(code),
    onSuccess: () => {
      toast.success('MFA activado correctamente');
      setShowMFASetup(false);
      refreshUser?.();
    },
    onError: () => toast.error('Código inválido'),
  });

  // Mutation disable MFA
  const disableMFAMutation = useMutation({
    mutationFn: (code: string) => authService.disableMFA(code),
    onSuccess: () => {
      toast.success('MFA desactivado');
      refreshUser?.();
    },
    onError: () => toast.error('Código inválido'),
  });

  const onChangePassword = (data: { currentPassword: string; newPassword: string }) => {
    changePasswordMutation.mutate(data);
  };

  const handleVerifyMFA = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const code = formData.get('mfaCode') as string;
    if (code?.length === 6) {
      verifyMFAMutation.mutate(code);
    }
  };

  const handleDisableMFA = () => {
    const code = prompt('Ingrese el código de su autenticador para desactivar MFA:');
    if (code?.length === 6) {
      disableMFAMutation.mutate(code);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
        <p className="text-gray-500 mt-1">Gestione su información y seguridad</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('datos')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'datos'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Datos Personales
          </button>
          <button
            onClick={() => setActiveTab('seguridad')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'seguridad'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Seguridad
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'datos' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-medium">Información Personal</h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Avatar y nombre */}
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-primary-600">
                  {user.nombre?.[0]}{user.apellido?.[0]}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{user.nombre} {user.apellido}</h3>
                <p className="text-gray-500">{user.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800`}>
                  {rolLabels[user.rol] || user.rol}
                </span>
              </div>
            </div>

            <hr />

            {/* Datos */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Nombre</label>
                <p className="mt-1 text-gray-900">{user.nombre}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Apellido</label>
                <p className="mt-1 text-gray-900">{user.apellido}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">DNI</label>
                <p className="mt-1 text-gray-900">{(user as any).dni || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Rol</label>
                <p className="mt-1 text-gray-900">{rolLabels[user.rol] || user.rol}</p>
              </div>
              {user.partidoId && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Partido</label>
                  <p className="mt-1 text-gray-900">Partido asignado</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p>
                Para modificar sus datos personales, contacte al administrador del sistema.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'seguridad' && (
        <div className="space-y-6">
          {/* Cambiar contraseña */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-medium">Cambiar Contraseña</h2>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitPwd(onChangePassword)} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium mb-1">Contraseña Actual</label>
                  <input
                    type="password"
                    {...registerPwd('currentPassword', { required: 'Requerido' })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  {errorsPwd.currentPassword && <p className="text-red-500 text-sm mt-1">{errorsPwd.currentPassword.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nueva Contraseña</label>
                  <input
                    type="password"
                    {...registerPwd('newPassword', { 
                      required: 'Requerido',
                      minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                      pattern: { 
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Debe contener mayúscula, minúscula y número'
                      }
                    })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  {errorsPwd.newPassword && <p className="text-red-500 text-sm mt-1">{errorsPwd.newPassword.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Confirmar Nueva Contraseña</label>
                  <input
                    type="password"
                    {...registerPwd('confirmPassword', { 
                      required: 'Requerido',
                      validate: value => value === newPassword || 'Las contraseñas no coinciden'
                    })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  {errorsPwd.confirmPassword && <p className="text-red-500 text-sm mt-1">{errorsPwd.confirmPassword.message}</p>}
                </div>
                <button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {changePasswordMutation.isPending ? 'Guardando...' : 'Cambiar Contraseña'}
                </button>
              </form>
            </div>
          </div>

          {/* MFA */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-medium">Autenticación de Dos Factores (MFA)</h2>
            </div>
            <div className="p-6">
              {user.mfaHabilitado ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-green-600">
                    <span className="text-2xl">🔐</span>
                    <div>
                      <p className="font-medium">MFA Activado</p>
                      <p className="text-sm text-gray-500">Su cuenta tiene protección adicional</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDisableMFA}
                    disabled={disableMFAMutation.isPending}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Desactivar MFA
                  </button>
                </div>
              ) : showMFASetup ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Escanee el código QR con su aplicación de autenticación (Google Authenticator, Authy, etc.)
                  </p>
                  <div className="flex justify-center">
                    <img src={mfaQR} alt="QR Code MFA" className="w-48 h-48" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">O ingrese manualmente:</p>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{mfaSecret}</code>
                  </div>
                  <form onSubmit={handleVerifyMFA} className="flex gap-2 max-w-xs mx-auto">
                    <input
                      type="text"
                      name="mfaCode"
                      placeholder="Código de 6 dígitos"
                      maxLength={6}
                      className="flex-1 border rounded-lg px-3 py-2 text-center"
                    />
                    <button
                      type="submit"
                      disabled={verifyMFAMutation.isPending}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                    >
                      Verificar
                    </button>
                  </form>
                  <button
                    onClick={() => setShowMFASetup(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    La autenticación de dos factores agrega una capa extra de seguridad a su cuenta.
                  </p>
                  <button
                    onClick={() => setupMFAMutation.mutate()}
                    disabled={setupMFAMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {setupMFAMutation.isPending ? 'Configurando...' : 'Activar MFA'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sesiones */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-medium">Sesiones Activas</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-xl">💻</span>
                  <div>
                    <p className="font-medium text-gray-900">Sesión actual</p>
                    <p className="text-sm text-gray-500">Este dispositivo</p>
                  </div>
                </div>
                <span className="text-green-600 text-sm">Activa</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
